import { Box } from '@mui/material'
import { getDaysBetweenDates } from 'components/shared/processes'
import { aggregateAllGroupsToFetchData } from 'components/farm-dashboard/components/widgets/feed-consumption/hook/aggregateFeedConsumptionAllGroups'
import type { FeedConsumptionGroupItem } from 'components/farm-dashboard/components/widgets/feed-consumption/types'
import { Moment } from 'moment'
import { useEffect, useRef, useState } from 'react'
import GroupsApiService from 'services/groupsApiService'
import { COLORS } from 'styles/colors'
import Preloader from 'ui/Preloader'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import EmptyChartPlot from './EmptyChartPlot'
import { FeedPdfPoint } from './feedingAnalyticsPdf'
import FeedingApexConsumption from './FeedingApexConsumption'

type FeedTrendCardProps = {
	startDate: Moment
	endDate: Moment
	groupIds: number[]
	useAllGroups: boolean
	onData?: (points: FeedPdfPoint[]) => void
}

const columnWidthForCount = (count: number) => {
	if (count > 31) return '14px'
	if (count > 20) return '18px'
	return '36px'
}

const legend = [
	{ color: COLORS.main, label: 'Роздано корма' },
	{ color: COLORS.error, label: 'Остаток' },
	{ color: COLORS.inputBorder, label: 'Нет данных' },
]

const FeedTrendCard = ({
	startDate,
	endDate,
	groupIds,
	useAllGroups,
	onData,
}: FeedTrendCardProps) => {
	const onDataRef = useRef(onData)
	onDataRef.current = onData
	const [fetchData, setFetchData] = useState<FeedPdfPoint[]>([])
	const [loading, setLoading] = useState(true)
	const start = startDate.clone().startOf('day').valueOf()
	const end = endDate.clone().endOf('day').valueOf()
	const daysBetweenDates = getDaysBetweenDates({
		startDate: start,
		endDate: end,
	})
	const groupsKey = groupIds.join(',')
	const hasFeedData = fetchData.some(
		point => (point.distributed_sum ?? 0) > 0 || (point.cleaned_sum ?? 0) > 0,
	)

	useEffect(() => {
		onDataRef.current?.(fetchData)
	}, [fetchData])

	useEffect(() => {
		let cancelled = false
		setLoading(true)

		const load = async () => {
			if (useAllGroups) {
				const response = await GroupsApiService.getDataForConsuptionChartAll(
					start,
					end,
				)
				const groups: FeedConsumptionGroupItem[] = Array.isArray(response.data)
					? response.data
					: []
				return aggregateAllGroupsToFetchData(groups, start, end).fetchData
			}

			const groups = await Promise.all(
				groupIds.map(async id => {
					try {
						const response = await GroupsApiService.getDataForConsuptionChart(
							id,
							start,
							end,
						)
						const item = Array.isArray(response.data)
							? response.data[0]
							: response.data
						return {
							title: item?.title ?? '',
							dots: Array.isArray(item?.dots) ? item.dots : [],
						}
					} catch {
						return { title: '', dots: [] }
					}
				}),
			)
			return aggregateAllGroupsToFetchData(groups, start, end).fetchData
		}

		load()
			.then(data => {
				if (!cancelled) setFetchData(data)
			})
			.catch(() => {
				if (!cancelled) setFetchData([])
			})
			.finally(() => {
				if (!cancelled) setLoading(false)
			})

		return () => {
			cancelled = true
		}
		// groupsKey отражает groupIds, сам массив каждый рендер новый
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [end, groupsKey, start, useAllGroups])

	return (
		<Box
			sx={{
				flex: '1 1 100%',
				width: '100%',
				height: '390px',
				p: '24px',
				borderRadius: '12px',
				backgroundColor: COLORS.white,
				boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
				boxSizing: 'border-box',
				display: 'flex',
				flexDirection: 'column',
				overflow: 'visible',
			}}
		>
			<Box
				sx={{
					fontSize: '20px',
					fontWeight: 700,
					lineHeight: '24px',
					color: COLORS.black,
				}}
			>
				Расход корма и остатков
			</Box>
			<Box
				sx={{
					position: 'relative',
					flex: 1,
					minHeight: 0,
					mt: '8px',
					overflow: 'visible',
				}}
			>
				{loading ? (
					<Box
						sx={{
							height: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<Preloader />
					</Box>
				) : !hasFeedData ? (
					<>
						<TypographySecondary sx={{ display: 'block', lineHeight: '15px' }}>
							Вес, кг
						</TypographySecondary>
						<EmptyChartPlot start={start} end={end} height={260} />
					</>
				) : (
					<FeedingApexConsumption
						chartId='feedingAnalyticsConsumptionChart'
						topRadiusOnly
						fetchData={fetchData}
						borderRadius={8}
						columnWidth={columnWidthForCount(fetchData.length)}
						daysBetweenDates={daysBetweenDates}
						height={300}
						yAxisGap
					/>
				)}
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'center', mt: '8px' }}>
				{legend.map(item => (
					<Box
						key={item.label}
						sx={{ display: 'flex', alignItems: 'center', mr: '16px' }}
					>
						<Box
							sx={{
								width: '12px',
								height: '12px',
								borderRadius: '50%',
								backgroundColor: item.color,
							}}
						/>
						<TypographySecondary sx={{ ml: '8px' }}>
							{item.label}
						</TypographySecondary>
					</Box>
				))}
			</Box>
		</Box>
	)
}

export default FeedTrendCard
